import { EllipsisVertical, SquarePlus, Share } from "lucide-react";
import { BaseSheet } from "@/components/common/BaseSheet";
import { Button } from "@/components/ui/button";
import type { AddToHomeScreenTarget } from "@/types/voice/more-actions";

type AddToHomeScreenSheetProps = AddToHomeScreenTarget & {
  open: boolean;
  onClose: () => void;
  platform: "ios" | "android";
  brandColor: string;
};

/**
 * The manual steps for pinning this genie to a phone's home screen — iOS has no
 * install prompt, and some Android browsers never offer one.
 */
export const AddToHomeScreenSheet = ({ open, onClose, platform, brandColor, genieName, iconUrl }: AddToHomeScreenSheetProps) => {
  const steps =
    platform === "ios"
      ? [
          { icon: Share, text: <>Tap the <strong>Share</strong> button in your browser bar.</> },
          { icon: SquarePlus, text: <>Scroll down and tap <strong>Add to Home Screen</strong>, then <strong>Add</strong>.</> },
        ]
      : [
          { icon: EllipsisVertical, text: <>Tap your browser's <strong>menu</strong> button.</> },
          { icon: SquarePlus, text: <>Tap <strong>Add to Home screen</strong> or <strong>Install app</strong>, then <strong>Add</strong>.</> },
        ];

  return (
    <BaseSheet
      isOpen={open}
      onClose={onClose}
      title="Add to your home screen"
      description={`Open ${genieName} in one tap, like an app.`}
      side="bottom"
      size="full"
      overlayClassName="z-[11990]"
      contentClassName="z-[12000] !max-h-[90dvh] rounded-t-2xl [&_button]:min-h-[44px] [&_button]:min-w-[44px]"
      footer={
        <div className="flex w-full pb-[max(8px,env(safe-area-inset-bottom))]">
          <Button type="button" className="w-full border-0 text-white shadow-sm" style={{ background: brandColor }} onClick={onClose}>
            Got it
          </Button>
        </div>
      }
    >
      <div className="flex items-center gap-3 rounded-2xl border border-genie-border bg-white p-3">
        <img src={iconUrl} alt="" className="h-12 w-12 shrink-0 rounded-xl object-contain" />
        <p className="min-w-0 truncate text-sm font-semibold text-[#222B45]">{genieName}</p>
      </div>
      <ol className="mt-4 space-y-3">
        {steps.map(({ icon: Icon, text }, index) => (
          <li key={index} className="flex items-start gap-3 text-sm leading-relaxed text-[#3D4557]">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
              style={{ background: `${brandColor}14`, color: brandColor }}
            >
              <Icon size={16} strokeWidth={2} aria-hidden="true" />
            </span>
            <span className="pt-1">
              <span className="sr-only">Step {index + 1}: </span>
              {text}
            </span>
          </li>
        ))}
      </ol>
    </BaseSheet>
  );
};
